import * as css from 'css/exports.module.scss';

import { AppLogoMetadata } from './AppLogoMetadata';
import { AppNode } from './AppNode';
import { createNodeFromReact } from '@antv/g6-react-node';
import { GraphLines } from '@security/graph-tools';
import { isEqual } from 'lodash';
import { ObjectNode } from '@security/application-overview/ObjectNode';
import { usePrevious } from '@hooks';

import { ComboDefinition, GraphEdge, Permission, Volume } from '@sonar-software/sonar-core';
import { DataSecurityAppLogos, DataSecurityAppName } from '@ui/applications';
import { EdgeStyle, GraphinData, IUserEdge, IUserNode, Layout } from '@antv/graphin/lib/typings/type';
import Graphin, { Behaviors, G6 } from '@antv/graphin';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';

export const anchorPoints = [ [ 0, 0 ],
	[ 0, 0.5 ],
	[ 0.1, 0 ],
	[ 0.2, 0 ],
	[ 0.3, 0 ],
	[ 0.4, 0 ],
	[ 0.5, 0 ],
	[ 0.6, 0 ],
	[ 0.7, 0 ],
	[ 0.8, 0 ],
	[ 0.9, 0 ],
	[ 1, 0 ],
	[ 1, 0.5 ],
	[ 0, 1 ],
	[ 0.1, 1 ],
	[ 0.2, 1 ],
	[ 0.3, 1 ],
	[ 0.4, 1 ],
	[ 0.5, 1 ],
	[ 0.6, 1 ],
	[ 0.7, 1 ],
	[ 0.8, 1 ],
	[ 0.9, 1 ],
	[ 1, 1 ] ];

G6.registerNode('app-center-display-image', {
	draw: (cfg, group) => {
		const nodeHeight = Array.isArray(cfg.size) ? cfg.size[ 1 ] : cfg.size ?? 30;
		const nodeWidth = Array.isArray(cfg.size) ? cfg.size[ 0 ] : cfg.size ?? 30;

		const outerrect = group?.addShape('rect', {
			attrs: {
				fill: '',
				height: nodeHeight + 20,
				stroke: '',
				width: nodeWidth + 20,
				x: -(nodeWidth + 20) / 2,
				y: -(nodeHeight + 20) / 2
			},
			draggable: true,
			name: 'node'
		});

		group?.addShape('rect', {
			attrs: {
				cursor: 'pointer',
				fill: '#FAFAFA',
				height: nodeHeight + 10,
				radius: 2,
				stroke: '#D9D9D9',
				width: nodeWidth + 10,
				x: -(nodeWidth + 10) / 2,
				y: -(nodeHeight + 10) / 2
			},
			draggable: true,
			name: 'border'
		});

		group?.addShape('image', {
			attrs: {
				cursor: 'pointer',
				height: nodeHeight,
				img: cfg.img,
				width: nodeWidth,
				x: -nodeWidth / 2,
				y: -nodeHeight / 2
			},
			draggable: true,
			name: 'image'
		});

		return outerrect;
	}
});

G6.registerNode('app-object', { ...createNodeFromReact(ObjectNode),
	getAnchorPoints() {
		return undefined;
	}
});

G6.registerNode('app-display', { ...createNodeFromReact(AppNode),
	getAnchorPoints() {
		return undefined;
	}
});

export interface registerEdgeProps {
	permissions: Permission,
	volume: Volume
}

const registerEdge = ({ permissions, volume }: registerEdgeProps) => {
	G6.registerEdge(
		`${permissions}-${volume}`,
		{
			afterDraw(_, group) {
				if (!group) {
					return;
				}

				const shape = group.getFirst() as GraphEdge;
				const startPoint = shape.getPoint(0);

				const circleCount = volume === 'low' ? 1 : 2;

				const combinations: Array<{permission: Permission, circleCountIndex: number}> = [];

				for (let i = 0; i < circleCount; i++) {
					combinations.push({
						circleCountIndex: i,
						permission: 'read'
					});
				}

				if (permissions === 'readwrite') {
					for (let i = 0; i < circleCount; i++) {
						combinations.push({
							circleCountIndex: i,
							permission: 'readwrite'
						});
					}
				}

				combinations.forEach((combination) => {
					const { permission, circleCountIndex } = combination;
					const circle = group.addShape('circle', {
						attrs: {
							fill: permission === 'read' ? css.read : css.write,
							r: 5,
							x: startPoint?.x,
							y: startPoint?.y
						},
						name: 'circle-shape'
					});

					const randomOffset = (Math.random() * 0.1) + 0.2;
					circle.animate(
						(ratio: number) => {
							let newRatio = ratio;

							if (permission === 'readwrite') {
								newRatio += circleCountIndex * randomOffset;

								newRatio = newRatio > 1 ? newRatio - 1 : newRatio;
							} else if (permission === 'read') {
								newRatio = 1 - newRatio;
								newRatio -= circleCountIndex * randomOffset;

								newRatio = newRatio < 0 ? newRatio + 1 : newRatio;
							}

							const tmpPoint = shape.getPoint(newRatio);

							return {
							  x: tmpPoint.x,
							  y: tmpPoint.y
							};
						},
						{
							duration: 3000,
							repeat: true
						}
					);
				});
			},
			setState(name, value, item) {
				if (name !== 'inactive') {
					return;
				}

				const children = item?.getContainer().getChildren() ?? [];
				const isActive = value === false;
				const color = isActive ? css.overviewStrokeColor : 'rgb(235,235,235)';

				if (children.length) {
					const [ path ] = children;
					path.attr('stroke', color);
					path.attr('startArrow', {
						...path.attr('startArrow'),
						fill: color
					});
					path.attr('endArrow', {
						...path.attr('endArrow'),
						fill: color
					});
				}
			},
			update: undefined
		},
		'quadratic'
	);
};

registerEdge({
	permissions: 'read',
	volume: 'low' });
registerEdge({
	permissions: 'read',
	volume: 'high' });
registerEdge({
	permissions: 'readwrite',
	volume: 'low' });
registerEdge({
	permissions: 'readwrite',
	volume: 'high' });

export function buildImageMetadata(id: number, name: string, label?: string): Promise<AppLogoMetadata> {
	const logoPath = DataSecurityAppLogos[ name as DataSecurityAppName ];

	if (!logoPath) {
		return Promise.resolve({
			id: id,
			label: label,
			name: name
		});
	}

	return new Promise((resolve) => {
		const logo = new Image();

		logo.onload = () => {
			resolve({
				height: logo.height,
				id: id,
				label: label,
				name: name,
				path: logoPath,
				width: logo.width
			});
		};

		logo.src = logoPath;
	});
}

export function buildEdge(read: number, write: number, lineType: string, source: string, target:string): IUserEdge {
	const threshhold = 7;
	let type = lineType;

	if (type === '') {
		if (write > 0) {
			if (write >= threshhold || read >= threshhold) {
				type = GraphLines[ 11 ].value;
			} else {
				type = GraphLines[ 10 ].value;
			}
		} else if (read >= threshhold) {
			type = GraphLines[ 9 ].value;
		} else {
			type = GraphLines[ 8 ].value;
		}
	}

	return {
		source: source,
		style: {
			endArrow: {
				fill: css.overviewStrokeColor,
				path: write ? G6.Arrow.triangle() : G6.Arrow.rect()
			},
			startArrow: {
				fill: css.overviewStrokeColor,
				path: read ? G6.Arrow.triangle() : G6.Arrow.rect()
			},
			stroke: css.overviewStrokeColor
		} as Partial<EdgeStyle>,
		target: target,
		type: type };
}

export function getTextWidth(text: string, font?: string) {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	if (!context) {
		return 0;
	}

	if (font) {
		context.font = font;
	}

	const metrics = context.measureText(text);

	return metrics.width;
}

export interface OverviewGraphProps {
    data: {combos: ComboDefinition[], edges: IUserEdge[], nodes: IUserNode[]},
    maxZoom: number,
    minZoom: number,
    layoutOptions: Layout
}

export const OverviewGraph: React.FC<PropsWithChildren<OverviewGraphProps>> = ({
	children,
	data,
	maxZoom,
	minZoom,
	layoutOptions
}) => {
	const graphRef = React.createRef<Graphin>();

	const [ graphData, setgraphData ] = useState<GraphinData>({
		combos: [],
		edges: [],
		nodes: []
	});
	const numNode = graphData.nodes.length;

	const distance = useCallback((linkDistance: number) => (linkDistance ?
		Math.max(linkDistance,
			Math.min(300, 225 + (numNode / 2))) :
		undefined), [ numNode ]);

	const [ layoutOpts, setLayoutOpts ] = useState<Layout>({
		...layoutOptions,
		linkDistance: distance(layoutOptions.linkDistance)
	});

	const previousLayout = usePrevious(layoutOptions, layoutOptions);

	useEffect(() => {
		if (data !== graphData) {
			setgraphData(data);
		}
	}, [ data, graphData ]);

	useEffect(() => {
		if (!isEqual(layoutOptions, previousLayout)) {
			setLayoutOpts({
				...layoutOptions,
				linkDistance: distance(layoutOptions.linkDistance)
			});
		}
	}, [ layoutOptions, distance, previousLayout ]);

	useEffect(() => {
		const graph = graphRef.current?.graph;

		if (!graph || graphData.nodes.length <= 0) {
			return;
		}

		graph.setMaxZoom(maxZoom);
		graph.setMinZoom(minZoom);

		graph.render();
	}, [ graphRef, graphData, layoutOpts, maxZoom, minZoom ]);

	const { ActivateRelations, DragCanvas, DragCombo, DragNode, ZoomCanvas } = Behaviors;

	return (
		<Graphin
			containerId='app-graph'
			data={ graphData }
			fitView
			fitViewPadding={ 24 }
			layout={ layoutOpts }
			ref={ graphRef }>
			<ActivateRelations activeState='activeState' />
			<DragCanvas />
			<DragNode onlyChangeComboSize />
			<DragCombo />
			<ZoomCanvas disabled />
			{ children }
		</Graphin>
	);
};
